import React from 'react';
import { useTranslation } from 'react-i18next';
import { Flag, Tag, Calendar as CalendarIcon, AtSign } from 'lucide-react';
import { useDateTime } from '../../contexts/DateTimeContext';

// Tiny SVG-ish illustrations rendered inside the step tooltip when the step
// references something the user can't see on the actual page yet (empty
// inbox, no habits set up, etc.). These aren't screenshots — they're cheap
// React mock-ups that show the *shape* of the feature so the body text
// doesn't read into thin air.

const Chip = ({ color, children }) => (
  <span
    className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium"
    style={{ backgroundColor: `${color}20`, color }}
  >
    {children}
  </span>
);

const LabelChips = () => {
  const { t } = useTranslation();
  return (
    <div className="mt-3 rounded-md border border-border bg-muted/40 px-3 py-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <AtSign className="h-3 w-3 text-muted-foreground" />
        <Chip color="#f59e0b">{t('ui_views.tutorials.sample_label_urgent')}</Chip>
        <Chip color="#3b82f6">{t('ui_views.tutorials.sample_label_coding')}</Chip>
        <Chip color="#10b981">{t('ui_views.tutorials.sample_label_home')}</Chip>
        <Chip color="#ef4444">{t('ui_views.tutorials.sample_label_meetings')}</Chip>
      </div>
      <div className="mt-2 flex items-center gap-2 text-[10px] text-muted-foreground">
        <Flag className="h-3 w-3 text-red-500" />
        <span className="font-mono">!1</span>
        <Flag className="h-3 w-3 text-orange-400" />
        <span className="font-mono">!2</span>
        <Flag className="h-3 w-3 text-yellow-400" />
        <span className="font-mono">!3</span>
        <Flag className="h-3 w-3 text-muted-foreground" />
        <span className="font-mono">!4</span>
      </div>
    </div>
  );
};

const StreakDemo = () => {
  const { t } = useTranslation();
  return (
    <div className="mt-3 rounded-md border border-border bg-muted/40 px-3 py-2">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium">{t('ui_views.tutorials.sample_habit')}</span>
        <span className="text-amber-500 font-semibold">{t('ui_views.tutorials.streak_days', { count: 7 })}</span>
      </div>
      <div className="mt-2 flex items-center gap-1">
        {[1, 1, 1, 1, 1, 1, 1, 0, 0].map((on, i) => (
          <span
            key={i}
            className={`h-3 w-3 rounded-sm ${on ? 'bg-amber-500' : 'bg-muted-foreground/20'}`}
          />
        ))}
      </div>
    </div>
  );
};

const NlpDemo = () => {
  const { t } = useTranslation();
  const { weekdayShortNames } = useDateTime();
  // Monday-first list: index 4 is Friday.
  const friday = weekdayShortNames(1)[4];
  return (
    <div className="mt-3 rounded-md border border-border bg-muted/40 px-3 py-2 font-mono text-[11px]">
      <div className="text-foreground/80">{t('ui_views.tutorials.nlp_title')} <span className="text-blue-400">{t('ui_views.tutorials.nlp_date')}</span> <span className="text-emerald-400">{t('ui_views.tutorials.nlp_project_tag')}</span> <span className="text-red-400">!1</span></div>
      <div className="mt-2 flex flex-wrap gap-1.5 text-[10px] not-italic font-sans">
        <span className="inline-flex items-center gap-1 rounded bg-blue-500/15 px-1.5 py-0.5 text-blue-400">
          <CalendarIcon className="h-2.5 w-2.5" /> {friday}
        </span>
        <span className="inline-flex items-center gap-1 rounded bg-emerald-500/15 px-1.5 py-0.5 text-emerald-400">
          <Tag className="h-2.5 w-2.5" /> {t('ui_views.tutorials.nlp_project')}
        </span>
        <span className="inline-flex items-center gap-1 rounded bg-red-500/15 px-1.5 py-0.5 text-red-400">
          <Flag className="h-2.5 w-2.5" /> P1
        </span>
      </div>
    </div>
  );
};

// Label and description are translation keys.
const VIEW_TABS_BY_TYPE = [
  { id: 'table',    label: 'tasks.views.table',    desc: 'ui_views.tutorials.view_desc_table' },
  { id: 'agenda',   label: 'tasks.views.agenda',   desc: 'ui_views.tutorials.view_desc_agenda' },
  { id: 'board',    label: 'tasks.views.board',    desc: 'ui_views.tutorials.view_desc_board',    premium: true },
  { id: 'calendar', label: 'tasks.views.calendar', desc: 'ui_views.tutorials.view_desc_calendar', premium: true },
  { id: 'gallery',  label: 'tasks.views.gallery',  desc: 'ui_views.tutorials.view_desc_gallery',  premium: true },
];

const ViewsDemo = () => {
  const { t } = useTranslation();
  return (
    <div className="mt-3 grid grid-cols-1 gap-1 rounded-md border border-border bg-muted/40 px-3 py-2 text-[10px]">
      {VIEW_TABS_BY_TYPE.map((v) => (
        <div key={v.id} className="flex items-center justify-between gap-2">
          <span className={v.premium ? 'text-muted-foreground' : 'font-medium text-foreground'}>
            {t(v.label)}
          </span>
          <span className="text-muted-foreground">{t(v.desc)}{v.premium ? ` · ${t('ui_views.tutorials.in_projects')}` : ''}</span>
        </div>
      ))}
    </div>
  );
};

const VISUALS = {
  label_chips: LabelChips,
  nlp_demo:    NlpDemo,
  streak_demo: StreakDemo,
  views_demo:  ViewsDemo,
};

const StepVisual = ({ slug }) => {
  if (!slug) return null;
  const Component = VISUALS[slug];
  if (!Component) return null;
  return <Component />;
};

export default StepVisual;
